import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiBell, FiMail, FiInfo, FiMessageSquare, FiAlertCircle, FiTrash2 } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';

const Navbar = () => {
  const user = useSelector((state) => state.auth?.user);
  
  const [activePopup, setActivePopup] = useState(null); 
  const [currentTime, setCurrentTime] = useState(new Date());
  const navRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActivePopup(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const togglePopup = (popupName) => {
    setActivePopup(activePopup === popupName ? null : popupName);
  };

  // ==========================================
  // TEST FUNCTION: CLEAR ALL STORAGE
  // ==========================================
  const handleClearStorage = () => {
    if (window.confirm("Are you sure you want to clear all Local and Session storage?")) {
      localStorage.clear();
      sessionStorage.clear();
      alert("Storage Cleared! Reloading page...");
      window.location.reload();
    }
  };

  const popupVariants = {
    hidden: { opacity: 0, y: 10, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 300, damping: 25 } },
    exit: { opacity: 0, y: 10, scale: 0.95, transition: { duration: 0.2 } }
  };

  const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username : 'Guest User';
  const userRole = user?.role || 'Member';
  
  let avatarUrl = user?.profilePhoto?.[0]?.url || user?.avatar;
  if (!avatarUrl) {
    const gender = user?.gender?.toLowerCase();
    if (gender === 'female') {
      avatarUrl = 'https://cdn-icons-png.flaticon.com/512/4140/4140047.png'; 
    } else {
      avatarUrl = 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png'; 
    }
  }

  return (
    <nav 
      ref={navRef}
      style={{ background: 'linear-gradient(135deg, #7f083c 0%, #9a0a4a 100%)' }} 
      className="shadow-lg sticky top-0 z-50 w-full"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 relative">
        <div className="flex justify-between h-16 items-center">
          
          {/* ================= 1. LEFT: LOGO & TEXT ================= */}
          <Link to="/" className="flex items-center w-1/3" style={{ textDecoration: 'none' }}>
            <motion.div 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-shrink-0 bg-white rounded-full p-1 shadow-sm flex items-center justify-center" 
              style={{ width: '45px', height: '45px' }}
            >
              <img
                src="/favicon.ico" 
                alt="Seminary Logo"
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'contain' }}
              />
            </motion.div>

            <div className="ml-3 flex flex-col items-center justify-center">
              <span className="text-white font-bold tracking-wide text-center" style={{ fontSize: '1.05rem', textShadow: '1px 1px 2px rgba(0,0,0,0.3)', lineHeight: '1.2' }}>
                SHEPHERDS BIBLICAL WEB SEMINARY
              </span>
              <span className="italic font-semibold text-center" style={{ fontSize: '0.70rem', color: '#ffddee', lineHeight: '1.2', marginTop: '2px' }}>
                SHEPHERDING THE SHEPHERDS
              </span>
            </div>
          </Link>

          {/* ================= 2. CENTER: LIVE DIGITAL CLOCK ================= */}
          <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 flex-col items-center justify-center pointer-events-none">
            <motion.div
              animate={{ 
                textShadow: [
                  "0px 0px 5px rgba(253, 224, 71, 0.5)", 
                  "0px 0px 15px rgba(253, 224, 71, 1)", 
                  "0px 0px 5px rgba(253, 224, 71, 0.5)"
                ] 
              }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="text-xl font-bold text-yellow-300 tracking-widest font-mono drop-shadow-md"
            >
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
            </motion.div>
            <div className="text-[10px] text-pink-200 font-medium tracking-widest uppercase mt-0.5">
              {currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>

          {/* ================= 3. RIGHT: ICONS & PROFILE ================= */}
          <div className="flex items-center justify-end w-1/3 space-x-4 sm:space-x-5">
            
            {/* --- TEST BUTTON: CLEAR STORAGE --- */}
            {['admin', 'super_admin', 'superadmin', 'church_admin', 'staff', 'faculty'].includes(String(userRole).toLowerCase()) &&
              <button type="button" className="rounded-lg px-3 py-2 text-xs text-white border border-white/30" onClick={() => window.dispatchEvent(new CustomEvent('meeting-recording-uploads-open'))}>Recording uploads</button>}
            <button 
              onClick={handleClearStorage} 
              className="hidden md:flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-[10px] font-bold tracking-wider shadow-md transition-colors"
              title="Test Button: Clear All Storage"
            >
              <FiTrash2 size={12} /> CLEAR STORAGE
            </button>
            
            {/* --- MAIL ICON --- */}
            <div className="relative">
              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => togglePopup('mail')} className="text-white focus:outline-none relative">
                <FiMail size={22} className="opacity-90 hover:opacity-100" />
                <span className="absolute -top-1.5 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center shadow-md">3</span>
              </motion.button>
              <AnimatePresence>
                {activePopup === 'mail' && (
                  <motion.div variants={popupVariants} initial="hidden" animate="visible" exit="exit" className="absolute right-0 mt-3 w-72 bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                      <span className="font-semibold text-gray-800 text-sm">New Messages</span>
                    </div>
                    <div className="p-4 text-center text-sm text-gray-500">No new messages</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* --- NOTIFICATION ICON --- */}
            <div className="relative">
              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => togglePopup('notification')} className="text-white focus:outline-none relative">
                <FiBell size={22} className="opacity-90 hover:opacity-100" />
                <span className="absolute -top-1.5 -right-2 bg-yellow-400 text-red-900 text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center shadow-md">5</span>
              </motion.button>
               <AnimatePresence>
                {activePopup === 'notification' && (
                  <motion.div variants={popupVariants} initial="hidden" animate="visible" exit="exit" className="absolute right-0 mt-3 w-80 bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                      <span className="font-semibold text-gray-800 text-sm">Notifications</span>
                    </div>
                    <div className="p-4 text-center text-sm text-gray-500">You have new alerts!</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* --- VERTICAL DIVIDER --- */}
            <div className="h-8 w-px bg-white/20 mx-2 hidden sm:block"></div>

            {/* --- USER PROFILE SECTION --- */}
            <div className="flex items-center space-x-3 cursor-pointer">
              <div className="hidden md:flex flex-col text-right">
                <span className="text-[13px] font-bold text-white leading-tight">
                  {userName}
                </span>
                <span className="text-[10px] font-medium text-pink-200 capitalize">
                  {userRole.replace('_', ' ')}
                </span>
              </div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="h-10 w-10 rounded-full overflow-hidden border-2 border-pink-300 shadow-lg bg-white"
              >
                <img 
                  src={avatarUrl} 
                  alt="Profile" 
                  className="h-full w-full object-cover"
                />
              </motion.div>
            </div>

          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;